import type { ISODateTime } from './inventory';
import type { FieldOpsTeam } from './fieldOpsTeam';

/**
 * A Field Ops work-queue item -- the lightweight system Field Ops uses to
 * organize, prioritize, assign and measure day-to-day work (issue #7957).
 * Backed by leatherback's FieldWorkRequestsService, which replaced the
 * narrower ShipmentRequestsService in place: shipping is now one optional
 * part of a request (see FieldWorkLogistics), not the request type.
 *
 * Label maps live here, in the domain module, not in components -- see
 * AGENTS.md. Components read `statusLabels[status]` rather than
 * hand-rolling a switch.
 */

export type FieldWorkSource =
  'CUSTOMER_SUCCESS' | 'SALES' | 'ENGINEERING' | 'REMOTE_OPS' | 'AD_HOC';

export type FieldWorkPriority = 'URGENT' | 'HIGH' | 'NORMAL' | 'LOW';

export type FieldWorkStatus =
  'NEW' | 'TRIAGED' | 'IN_PROGRESS' | 'BLOCKED' | 'COMPLETE' | 'REJECTED';

export type FieldWorkLogisticsMode = 'NONE' | 'CARGONIZER' | 'HAND_CARRY' | 'OTHER';

export interface FieldWorkLogistics {
  mode: FieldWorkLogisticsMode;
  shipmentId?: string | undefined;
  notes?: string | undefined;
}

export type FieldWorkActivityKind = 'NOTE' | 'HANDOFF' | 'ASSIGNED';

/** One entry in a request's activity log, oldest first. Written by the server only. */
export interface FieldWorkActivity {
  id: string;
  kind: FieldWorkActivityKind;
  note?: string | undefined;
  /** Assignee before and after, for HANDOFF and ASSIGNED; '' is Unassigned. */
  fromAssignee?: string | undefined;
  toAssignee?: string | undefined;
  author?: string | undefined;
  createTime?: ISODateTime | undefined;
}

export interface FieldWorkRequest {
  id: string;
  title: string;
  description: string;
  source?: FieldWorkSource | undefined;
  customerId?: string | undefined;
  siteId?: string | undefined;
  relatedItemIds: string[];
  githubIssueUrl?: string | undefined;
  team: FieldOpsTeam;
  priority?: FieldWorkPriority | undefined;
  queuePosition: number;
  /** Empty string means Unassigned. */
  assignee: string;
  dueDate?: ISODateTime | undefined;
  status: FieldWorkStatus;
  blocker?: string | undefined;
  logistics?: FieldWorkLogistics | undefined;
  completionNotes?: string | undefined;
  requestedBy?: string | undefined;
  requestedAt: ISODateTime;
  // Server-managed workflow timestamps / metrics.
  triagedAt?: ISODateTime | undefined;
  startedAt?: ISODateTime | undefined;
  completedAt?: ISODateTime | undefined;
  blockedTotalSeconds: number;
  blockedSince?: ISODateTime | undefined;
  version: number;
  lastModified?: ISODateTime | undefined;
  lastModifiedBy?: string | undefined;
  activity: FieldWorkActivity[];
}

export type FieldWorkRequestCreateInput = {
  title: string;
  description?: string | undefined;
  source?: FieldWorkSource | undefined;
  customerId?: string | undefined;
  siteId?: string | undefined;
  relatedItemIds?: string[] | undefined;
  githubIssueUrl?: string | undefined;
  team?: FieldOpsTeam | undefined;
  priority?: FieldWorkPriority | undefined;
  dueDate?: ISODateTime | undefined;
  logistics?: FieldWorkLogistics | undefined;
};

// Server-set fields (requestedBy/At, the workflow timestamps, blocked
// accounting, lastModified*) are never patchable -- same reasoning as
// Shipment's own ShipmentPatch.
export type FieldWorkRequestPatch = Partial<
  Omit<
    FieldWorkRequest,
    | 'id'
    | 'version'
    | 'requestedBy'
    | 'requestedAt'
    | 'triagedAt'
    | 'startedAt'
    | 'completedAt'
    | 'blockedTotalSeconds'
    | 'blockedSince'
    | 'lastModified'
    | 'lastModifiedBy'
  >
> & {
  id: string;
  version: number;
};

// --- Display labels + ordering (read by components, never re-derived) ---

export const fieldWorkStatuses: readonly FieldWorkStatus[] = [
  'NEW',
  'TRIAGED',
  'IN_PROGRESS',
  'BLOCKED',
  'COMPLETE',
  'REJECTED',
];

// The Kanban columns, in order. REJECTED is terminal and filtered off the
// board by default, so it is deliberately not a column.
export const fieldWorkBoardColumns: readonly FieldWorkStatus[] = [
  'NEW',
  'TRIAGED',
  'IN_PROGRESS',
  'BLOCKED',
  'COMPLETE',
];

export const statusLabels: Record<FieldWorkStatus, string> = {
  NEW: 'New',
  TRIAGED: 'Triaged',
  IN_PROGRESS: 'In progress',
  BLOCKED: 'Blocked',
  COMPLETE: 'Complete',
  REJECTED: 'Rejected',
};

// Open = still expected to move; closed = done. Used for "backlog" counts
// and to decide when a due date is still meaningful.
export function isOpenStatus(status: FieldWorkStatus): boolean {
  return status !== 'COMPLETE' && status !== 'REJECTED';
}

// Allowed status moves (Sindhu, PR #7958). Forward-only through the
// lifecycle: once a request leaves NEW it never goes back, and TRIAGED
// can't return to NEW. REJECTED and COMPLETE each keep one reopen path so
// a mistake is recoverable without restarting triage. Enforced in the
// backend (UpdateFieldWorkRequest) and mirrored here for the UI + mock.
export const statusTransitions: Record<FieldWorkStatus, readonly FieldWorkStatus[]> = {
  NEW: ['TRIAGED', 'REJECTED'],
  TRIAGED: ['IN_PROGRESS', 'BLOCKED', 'REJECTED'],
  IN_PROGRESS: ['BLOCKED', 'COMPLETE', 'REJECTED'],
  BLOCKED: ['IN_PROGRESS', 'COMPLETE', 'REJECTED'],
  COMPLETE: ['IN_PROGRESS'],
  REJECTED: ['TRIAGED'],
};

export function canTransitionStatus(from: FieldWorkStatus, to: FieldWorkStatus): boolean {
  return from === to || statusTransitions[from].includes(to);
}

export const fieldWorkPriorities: readonly FieldWorkPriority[] = [
  'URGENT',
  'HIGH',
  'NORMAL',
  'LOW',
];

export const priorityLabels: Record<FieldWorkPriority, string> = {
  URGENT: 'Urgent',
  HIGH: 'High',
  NORMAL: 'Normal',
  LOW: 'Low',
};

// Lower sorts first in "My Work".
export const priorityRank: Record<FieldWorkPriority, number> = {
  URGENT: 0,
  HIGH: 1,
  NORMAL: 2,
  LOW: 3,
};

export const fieldWorkSources: readonly FieldWorkSource[] = [
  'CUSTOMER_SUCCESS',
  'SALES',
  'ENGINEERING',
  'REMOTE_OPS',
  'AD_HOC',
];

export const sourceLabels: Record<FieldWorkSource, string> = {
  CUSTOMER_SUCCESS: 'Customer Success',
  SALES: 'Sales',
  ENGINEERING: 'Engineering',
  REMOTE_OPS: 'Remote Ops',
  AD_HOC: 'Ad hoc',
};

export const fieldWorkLogisticsModes: readonly FieldWorkLogisticsMode[] = [
  'NONE',
  'CARGONIZER',
  'HAND_CARRY',
  'OTHER',
];

export const logisticsModeLabels: Record<FieldWorkLogisticsMode, string> = {
  NONE: 'No shipping required',
  CARGONIZER: 'Cargonizer',
  HAND_CARRY: 'Drive / hand carry',
  OTHER: 'Other',
};

// Sort key for a team queue / My Work: ranked items first (by
// queuePosition), then unranked; ties broken by priority then due date.
export function compareQueueOrder(a: FieldWorkRequest, b: FieldWorkRequest): number {
  const aRanked = a.queuePosition > 0;
  const bRanked = b.queuePosition > 0;
  if (aRanked && bRanked && a.queuePosition !== b.queuePosition) {
    return a.queuePosition - b.queuePosition;
  }
  if (aRanked !== bRanked) return aRanked ? -1 : 1;
  const aPriority = a.priority ? priorityRank[a.priority] : 99;
  const bPriority = b.priority ? priorityRank[b.priority] : 99;
  if (aPriority !== bPriority) return aPriority - bPriority;
  return (a.dueDate ?? '￿').localeCompare(b.dueDate ?? '￿');
}

/** Longest note the server accepts; longer discussion belongs in GitHub. */
export const MAX_NOTE_LENGTH = 4000;

/**
 * The handoff that gave the request to its current assignee, if the
 * latest assignee change was a handoff. Shown as "Handed to you by …".
 */
export function currentHandoff(request: FieldWorkRequest): FieldWorkActivity | undefined {
  for (let i = request.activity.length - 1; i >= 0; i -= 1) {
    const entry = request.activity[i];
    if (!entry || entry.kind === 'NOTE') continue;
    return entry.kind === 'HANDOFF' && entry.toAssignee === request.assignee ? entry : undefined;
  }
  return undefined;
}

export function canHandOff(request: Pick<FieldWorkRequest, 'status'>): boolean {
  return request.status !== 'COMPLETE' && request.status !== 'REJECTED';
}
